/**
 * Message rendering: MessageBubble and its part views (prompt.md §30, §48, §77).
 *
 * WHY parts are rendered individually: a message is provider-neutral, so it can
 * contain text, reasoning, images, file artifacts, tool calls and tool results.
 * The transcript must show tool traffic as first-class content — that is what
 * makes a long-running agent run understandable — while keeping reasoning
 * visually distinct from what the agent said out loud.
 */

import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { MessagePart, MessageRole } from "@dayvise/domain";

import { MUTED_TEXT, rowStyle, stackStyle } from "../styles.ts";
import { describeMessagePart, formatArguments, messageCopyText, messageRoleLabel, summarizeToolResult, toolLabel } from "../logic/messageParts.ts";
import { renderUserTimestamp, type TimestampMode } from "../logic/format.ts";
import { Badge } from "../primitives/Feedback.tsx";
import { Button } from "../primitives/Button.tsx";

const BUBBLE_PADDING = "var(--dayvise-space-md)";

function bubbleBackground(role: MessageRole): string {
  if (role === "user") return "color-mix(in srgb, var(--dayvise-color-accent) 8%, var(--dayvise-color-raised))";
  if (role === "system" || role === "developer") return "var(--dayvise-color-surface)";
  return "var(--dayvise-color-raised)";
}

/** Renders one tool call as a labelled, collapsed-by-default block of arguments. */
export function ToolPartView({ part }: { readonly part: Extract<MessagePart, { readonly type: "tool_call" }> }) {
  return (
    <div style={{ ...stackStyle("var(--dayvise-space-xxs)"), borderLeft: "2px solid var(--dayvise-color-border)", paddingLeft: "var(--dayvise-space-sm)" }}>
      <span style={{ ...rowStyle("var(--dayvise-space-xs)"), fontSize: "var(--dayvise-font-size-sm)" }}>
        <Badge tone="neutral">{toolLabel(part.toolId)}</Badge>
        <code style={{ fontFamily: "var(--dayvise-font-family-mono)", fontSize: "var(--dayvise-font-size-xs)", color: "var(--dayvise-color-text-muted)" }}>{part.toolId}</code>
      </span>
      <pre
        style={{
          margin: 0,
          padding: "var(--dayvise-space-sm)",
          background: "var(--dayvise-color-surface)",
          borderRadius: "var(--dayvise-radius-sm)",
          fontFamily: "var(--dayvise-font-family-mono)",
          fontSize: "var(--dayvise-font-size-xs)",
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
        }}
      >
        {formatArguments(part.arguments)}
      </pre>
    </div>
  );
}

/** Renders one part. Unknown shapes were already rejected by the descriptor. */
export function MessagePartView({ part, onDownloadFile, downloadingFileId }: { readonly part: MessagePart; readonly onDownloadFile?: (fileId: string) => void; readonly downloadingFileId?: string | null }) {
  const descriptor = describeMessagePart(part);
  switch (descriptor.kind) {
    case "text":
      return <p style={{ margin: 0, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{descriptor.text}</p>;
    case "reasoning":
      return (
        <details style={{ ...MUTED_TEXT, fontStyle: "italic" }}>
          <summary style={{ cursor: "pointer" }}>Reasoning</summary>
          <p style={{ margin: "var(--dayvise-space-xs) 0 0", whiteSpace: "pre-wrap" }}>{descriptor.text}</p>
        </details>
      );
    case "image":
      return <img src={descriptor.url} alt={descriptor.alt} style={{ maxWidth: "100%", borderRadius: "var(--dayvise-radius-md)" }} />;
    case "file":
      return (
        <span style={rowStyle("var(--dayvise-space-xs)")}>
          <Badge tone="neutral">{descriptor.mimeType}</Badge>
          <span style={{ fontSize: "var(--dayvise-font-size-sm)" }}>{descriptor.name}</span>
          {onDownloadFile && part.type === "file" ? <Button size="sm" disabled={!!downloadingFileId} onClick={() => onDownloadFile(part.fileId)}>{downloadingFileId === part.fileId ? "Downloading…" : "Download"}</Button> : null}
        </span>
      );
    case "toolCall":
      return <ToolPartView part={part as Extract<MessagePart, { readonly type: "tool_call" }>} />;
    case "toolResult":
      return (
        <span style={{ ...stackStyle("var(--dayvise-space-xxs)"), borderLeft: `2px solid ${descriptor.ok ? "var(--dayvise-color-success)" : "var(--dayvise-color-danger)"}`, paddingLeft: "var(--dayvise-space-sm)" }}>
          <Badge tone={descriptor.ok ? "success" : "danger"}>{descriptor.ok ? "Tool succeeded" : "Tool failed"}</Badge>
          <code style={{ fontFamily: "var(--dayvise-font-family-mono)", fontSize: "var(--dayvise-font-size-xs)", overflowWrap: "anywhere" }}>{descriptor.summary}</code>
        </span>
      );
    case "secret":
      return (
        <span style={rowStyle("var(--dayvise-space-xs)")}>
          <Badge tone="neutral">secret</Badge>
          <span style={{ ...MUTED_TEXT, fontSize: "var(--dayvise-font-size-sm)" }}>
            {descriptor.label} — sealed, never sent to any model
          </span>
        </span>
      );
    default: {
      const exhausted: never = descriptor;
      return <span style={MUTED_TEXT}>{JSON.stringify(exhausted)}</span>;
    }
  }
}

export interface MessageBubbleProps {
  readonly role: MessageRole;
  readonly parts: readonly MessagePart[];
  readonly showReasoning?: boolean;
  readonly onDownloadFile?: (fileId: string) => void;
  readonly downloadingFileId?: string | null;
  readonly createdAt?: string;
  /** The signed-in user's timestamp preference; hidden renders nothing at all. */
  readonly timestampMode?: TimestampMode;
  /** Shows a typing affordance while deltas are still arriving (prompt.md §77). */
  readonly streaming?: boolean;
  readonly authorLabel?: string;
  readonly footer?: ReactNode;
  /**
   * Copies this message's text. Supplied by the client, so the copy control uses
   * the platform clipboard (and its permission errors) rather than a second one.
   */
  readonly onCopy?: (text: string) => void | Promise<void>;
}

export function MessageBubble({ role, parts, showReasoning = true, onDownloadFile, downloadingFileId, createdAt, timestampMode = "relative", streaming = false, authorLabel, footer, onCopy }: MessageBubbleProps) {
  const copyable = messageCopyText(parts);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (resetTimer.current !== null) clearTimeout(resetTimer.current);
  }, []);
  const copy = useCallback(async () => {
    if (onCopy === undefined) return;
    try {
      await onCopy(copyable);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
    if (resetTimer.current !== null) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setCopyState("idle"), 4_000);
  }, [copyable, onCopy]);
  const copyLabel = copyState === "copied" ? "Copied" : copyState === "failed" ? "Copy failed" : "Copy";
  return (
    <article
      aria-label={`${authorLabel ?? messageRoleLabel(role)} message`}
      style={{ ...stackStyle("var(--dayvise-space-sm)"), padding: BUBBLE_PADDING, borderRadius: "var(--dayvise-radius-lg)", background: bubbleBackground(role), border: "1px solid var(--dayvise-color-border)" }}
    >
      <header style={rowStyle("var(--dayvise-space-sm)", "space-between")}>
        <span style={{ fontSize: "var(--dayvise-font-size-xs)", fontWeight: "var(--dayvise-font-weight-semibold)", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--dayvise-color-text-muted)" }}>
          {authorLabel ?? messageRoleLabel(role)}
        </span>
        <span style={rowStyle("var(--dayvise-space-xs)")}>
          <span style={MUTED_TEXT}>{createdAt === undefined ? null : renderUserTimestamp(createdAt, timestampMode)}</span>
          {onCopy === undefined ? null : (
            // The label changes with the result and the wrapper announces it, so a
            // screen-reader user learns that the clipboard write succeeded or failed.
            <span aria-live="polite">
              <Button size="sm" variant="ghost" aria-label={`Copy ${messageRoleLabel(role)} message text`} disabled={copyable.length === 0} onClick={() => void copy()}>
                {copyLabel}
              </Button>
            </span>
          )}
        </span>
      </header>
      {parts.filter((part) => showReasoning || part.type !== "reasoning").map((part, index) => (
        <MessagePartView key={index} part={part} onDownloadFile={onDownloadFile} downloadingFileId={downloadingFileId} />
      ))}
      {streaming ? (
        <span aria-live="polite" style={MUTED_TEXT}>
          still writing…
        </span>
      ) : null}
      {footer}
    </article>
  );
}

/** Collapsed one-line preview for a tool result inside a dense transcript. */
export function toolResultPreview(content: unknown): string {
  return summarizeToolResult(content);
}
